<template>
  <div class="relative">
    <button @click="showDropdown = !showDropdown"
      class="flex items-center gap-2 px-3 py-2 text-white hover:text-green-200 transition-colors font-body text-sm"
      aria-label="Select language">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
      </svg>
      <span>{{ currentLanguageName }}</span>
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div v-if="showDropdown"
      class="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-creme-300 py-1 z-50 max-h-96 overflow-y-auto"
      @click.stop>
      <div v-if="regionalLanguages.length > 0" class="border-b border-creme-200 pb-1 mb-1">
        <div class="px-4 py-2 text-xs font-body font-semibold text-gray-500 uppercase">
          {{ regionLabel }}
        </div>
        <button v-for="code in regionalLanguages" :key="code" @click="selectLanguage(code)" :class="[
          'block w-full text-left px-4 py-2 text-sm transition-colors font-body',
          currentLanguage === code
            ? 'bg-creme-100 text-green-700 font-semibold'
            : 'text-primary hover:bg-creme-100'
        ]">
          {{ languages[code] || code }}
        </button>
      </div>
      <div>
        <div v-if="regionalLanguages.length > 0"
          class="px-4 py-2 text-xs font-body font-semibold text-gray-500 uppercase">
          All Languages
        </div>
        <button v-for="(name, code) in filteredLanguages" :key="code" @click="selectLanguage(code)" :class="[
          'block w-full text-left px-4 py-2 text-sm transition-colors font-body',
          currentLanguage === code
            ? 'bg-creme-100 text-green-700 font-semibold'
            : 'text-primary hover:bg-creme-100'
        ]">
          {{ name }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { translationService } from '../services/translation'

const showDropdown = ref(false)
const languages = ref({ en: 'English' })
const regionalLanguages = ref([])
const countryCode = ref(null)
const currentLanguage = ref(translationService.getLanguage())

const currentLanguageName = computed(() => {
  return languages.value[currentLanguage.value] || 'English'
})

const regionLabel = computed(() => {
  if (!countryCode.value) return 'Recommended'
  const regionNames = {
    'KE': 'Kenya',
    'TZ': 'Tanzania',
    'UG': 'Uganda',
    'RW': 'Rwanda',
    'BI': 'Burundi',
    'CD': 'DR Congo',
    'IN': 'India',
    'BD': 'Bangladesh',
    'PK': 'Pakistan',
    'TH': 'Thailand',
    'VN': 'Vietnam',
    'ID': 'Indonesia',
    'MY': 'Malaysia',
    'SG': 'Singapore',
    'ZA': 'South Africa',
    'ET': 'Ethiopia',
    'NG': 'Nigeria',
  }
  return regionNames[countryCode.value] || 'Your Region'
})

const filteredLanguages = computed(() => {
  const filtered = {}
  for (const [code, name] of Object.entries(languages.value)) {
    if (!regionalLanguages.value.includes(code)) {
      filtered[code] = name
    }
  }
  return filtered
})

const selectLanguage = async (code) => {
  currentLanguage.value = code
  translationService.setLanguage(code)
  showDropdown.value = false
}

const closeDropdown = (e) => {
  if (!e.target.closest('.relative')) {
    showDropdown.value = false
  }
}

onMounted(async () => {
  const response = await translationService.getSupportedLanguages()
  languages.value = response.all || response
  regionalLanguages.value = response.regional || []
  if (response.country_code) {
    countryCode.value = response.country_code
  }
  document.addEventListener('click', closeDropdown)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
})
</script>

<style scoped></style>
